// Turns the full network (nodes + edges from useNetworkGraph) into the subset a
// given screen renders. Callers pass a context and get back a clean
// { nodes, edges } — they never reach into node.degree / edge.secondary / the
// manual-branch flags themselves.
//
// Contexts:
//   "near"   → you + degree-1 + manual branches (dashboard, empty search)
//   "search" → "near" plus the matched degree-2 targets (active search)
//
// Edges are always pruned to those whose endpoints both survive the node filter,
// so no view can render a dangling edge.

function isNear(node) {
  return node.degree <= 1 || node.manualBranch;
}

function isNearEdge(edge) {
  return edge.degree === 1 || edge.manualBranch;
}

// d3 rewrites edge endpoints from ids to node objects in place; accept both.
function endId(end) {
  return typeof end === "object" && end ? end.id : end;
}

function keepEdges(edges, nodeIds) {
  return edges.filter(
    (e) => nodeIds.has(endId(e.source)) && nodeIds.has(endId(e.target))
  );
}

/**
 * @param nodes  full node list
 * @param edges  full edge list
 * @param opts   { context?: "near"|"search", matchedIds?: Set<string> }
 * @returns { nodes, edges } the subset to render
 */
export function graphView(nodes, edges, opts = {}) {
  const { context = "near", matchedIds = null } = opts;
  const searching = context === "search" && matchedIds && matchedIds.size > 0;

  const visibleNodes = searching
    ? nodes.filter((n) => isNear(n) || matchedIds.has(n.id))
    : nodes.filter(isNear);

  const nodeIds = new Set(visibleNodes.map((n) => n.id));

  // When searching we keep every edge between surviving nodes (so paths to a
  // matched degree-2 node show); otherwise only the near edges.
  const candidateEdges = searching ? edges : edges.filter(isNearEdge);
  const visibleEdges = keepEdges(candidateEdges, nodeIds);

  return { nodes: visibleNodes, edges: visibleEdges };
}
